'use client';

import { useEffect, useState } from 'react';

interface MineralPrice {
  name: string;
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
}

const INITIAL_PRICES: MineralPrice[] = [
  { name: 'Gold', symbol: 'AU', price: 2045.50, change: 12.30, changePercent: 0.61 },
  { name: 'Cobalt', symbol: 'CO', price: 32.75, change: -0.85, changePercent: -2.53 },
  { name: 'Copper', symbol: 'CU', price: 8.42, change: 0.15, changePercent: 1.81 },
  { name: 'Tantalum', symbol: 'TA', price: 185.20, change: 3.40, changePercent: 1.87 },
  { name: 'Lithium', symbol: 'LI', price: 24.60, change: -1.20, changePercent: -4.65 },
  { name: 'Nickel', symbol: 'NI', price: 18.35, change: 0.45, changePercent: 2.51 },
  { name: 'Silver', symbol: 'AG', price: 23.85, change: 0.32, changePercent: 1.36 },
  { name: 'Platinum', symbol: 'PT', price: 925.40, change: -5.60, changePercent: -0.60 },
];

export default function MineralPricesTicker() {
  const [prices, setPrices] = useState<MineralPrice[]>(INITIAL_PRICES);

  // Simulate realtime price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setPrices(prevPrices =>
        prevPrices.map(mineral => {
          // Random price fluctuation between -0.5% and +0.5%
          const fluctuation = (Math.random() - 0.5) * 0.01;
          const newPrice = mineral.price * (1 + fluctuation);
          const priceChange = newPrice - mineral.price;
          const percentChange = (priceChange / mineral.price) * 100;

          return {
            ...mineral,
            price: parseFloat(newPrice.toFixed(2)),
            change: parseFloat(priceChange.toFixed(2)),
            changePercent: parseFloat(percentChange.toFixed(2)),
          };
        })
      );
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Duplicate prices for seamless infinite scroll
  const duplicatedPrices = [...prices, ...prices];

  return (
    <div className="w-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-y border-golden/20 shadow-lg overflow-hidden">
      <div className="relative h-16">
        {/* Animated scrolling container */}
        <div className="absolute inset-0 flex items-center">
          <div className="flex animate-scroll-ticker gap-8 px-4">
            {duplicatedPrices.map((mineral, index) => (
              <div
                key={`${mineral.symbol}-${index}`}
                className="flex items-center gap-3 min-w-fit px-4 py-2 bg-gray-800/50 rounded border border-golden/10 backdrop-blur-sm"
              >
                {/* Symbol */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-golden to-silver flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-900">
                      {mineral.symbol}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-white font-mono">
                    {mineral.name}
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-base font-bold text-golden font-mono">
                    ${mineral.price.toFixed(2)}
                  </span>
                </div>

                {/* Change */}
                <div
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-semibold ${
                    mineral.change >= 0
                      ? 'bg-green-500/20 text-green-400' :'bg-red-500/20 text-red-400'
                  }`}
                >
                  <span>{mineral.change >= 0 ? '▲' : '▼'}</span>
                  <span>
                    {Math.abs(mineral.change).toFixed(2)} ({Math.abs(mineral.changePercent).toFixed(2)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gradient overlays for fade effect */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-900 to-transparent pointer-events-none z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-900 to-transparent pointer-events-none z-10"></div>
      </div>

      {/* Live indicator */}
      <div className="absolute top-2 right-4 flex items-center gap-2 z-20">
        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
        <span className="text-xs font-semibold text-green-400 uppercase tracking-wider font-mono">
          Live
        </span>
      </div>
    </div>
  );
}